import { useState, useEffect, useRef } from 'react'

import { useTranslation } from 'react-i18next'
import { connect } from 'scrivito'

import { FilterValues, TimeTrackingTab } from '../types'
import { FilterDropdownBody } from './FilterDropdownBody'
import {
  countActiveFilters,
  deriveInitialFilterState,
  InternalFilterState,
  resetCascades,
  toFilterValues,
} from './filterDropdownState'
import { useCascadingOptions } from './useCascadingOptions'
import { useDropdownClose } from './useDropdownClose'
import { Button } from '@/Atomic/Molecules/Button'
import { FoldingBox } from '@/Components/FoldingBox/FoldingBox'
import { WhoAmI } from '@/Data/WhoAmI/WhoAmIDataItem'
import { ensureString } from '@/utils/ensureString'

interface FilterOption {
  label: string
  value: string
}

interface TimeTrackingFilterDropdownProps {
  statusOptions: FilterOption[]
  teamOptions: FilterOption[]
  teamMemberOptions: FilterOption[]
  teamLeaderIds?: string[]
  initialFilters?: FilterValues
  onFilterChange: (filters: FilterValues) => void
  onTeamLeaderSelect: (leaderId: string) => void
  currentTab: TimeTrackingTab
  canFilterByTeam?: boolean
}

export const TimeTrackingFilterDropdown = connect(
  ({
    statusOptions,
    teamOptions,
    teamMemberOptions,
    teamLeaderIds,
    initialFilters,
    onFilterChange,
    onTeamLeaderSelect,
    currentTab,
    canFilterByTeam = true,
  }: TimeTrackingFilterDropdownProps) => {
    const { t } = useTranslation()

    const [isOpen, setIsOpen] = useState(false)
    const [state, setState] = useState<InternalFilterState>(() =>
      deriveInitialFilterState(initialFilters, teamLeaderIds),
    )
    const wrapperRef = useRef<HTMLDivElement>(null)

    const initialFiltersKey = JSON.stringify(initialFilters ?? null)
    const teamLeaderIdsKey = JSON.stringify(teamLeaderIds ?? [])

    useEffect(() => {
      const newState = deriveInitialFilterState(initialFilters, teamLeaderIds)
      setState(newState)
      if (newState.teamLeader) onTeamLeaderSelect(newState.teamLeader)
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialFiltersKey, teamLeaderIdsKey])

    useDropdownClose(wrapperRef, isOpen, () => setIsOpen(false))

    const currentUserId = ensureString(WhoAmI.get('contactId'))
    const { customerOptions, orderOptions, workPackageOptions } =
      useCascadingOptions({
        isOpen,
        customer: state.customer,
        order: state.order,
        leaderId: state.teamLeader,
        currentUserId,
      })

    const handleChange = (
      key: keyof InternalFilterState,
      value: string | null,
    ) => {
      if (key === 'teamLeader') onTeamLeaderSelect(value || '')
      setState((prev) => resetCascades(prev, key, value))
    }

    const handleApply = () => {
      onFilterChange(toFilterValues(state))
      setIsOpen(false)
    }

    const handleClear = () => {
      const cleared = deriveInitialFilterState(undefined)
      setState(cleared)
      onTeamLeaderSelect('')
      onFilterChange(toFilterValues(cleared))
    }

    const activeFilterCount = countActiveFilters(state, currentTab)

    return (
      <div className="time-tracking-filter-wrapper" ref={wrapperRef}>
        <Button
          variant="outlined"
          title={
            activeFilterCount > 0
              ? `${t('timeTracking.filter')} (${activeFilterCount})`
              : t('timeTracking.filter')
          }
          size="sm"
          onClick={() => setIsOpen(!isOpen)}
          icon="chevron-down"
        />
        <FoldingBox
          isBoxOpen={isOpen}
          close={() => setIsOpen(false)}
          body={
            <FilterDropdownBody
              currentTab={currentTab}
              canFilterByTeam={canFilterByTeam}
              state={state}
              statusOptions={statusOptions}
              teamOptions={teamOptions}
              teamMemberOptions={teamMemberOptions}
              customerOptions={customerOptions}
              orderOptions={orderOptions}
              workPackageOptions={workPackageOptions}
              onChange={handleChange}
              onApply={handleApply}
              onClear={handleClear}
            />
          }
        />
      </div>
    )
  },
)
